import { useCallback, useEffect, useState } from 'react';
import { notificationsAPI } from '../services/api';
import { useNotificationEvents } from '../context/WebSocketContext';
import type { NotificationItem } from '../types/sharing';

export function useNotifications() {
  const {
    unreadNotificationCount,
    latestNotification,
    setUnreadNotificationCount,
    clearLatestNotification,
  } = useNotificationEvents();

  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchNotifications = useCallback(async (unreadOnly = false) => {
    setLoading(true);
    setError(null);
    try {
      const data = await notificationsAPI.list(unreadOnly);
      setNotifications(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const data = await notificationsAPI.getUnreadCount();
      setUnreadNotificationCount(data.count);
      return data.count;
    } catch {
      return 0;
    }
  }, [setUnreadNotificationCount]);

  const markAsRead = useCallback(async (notificationId: number) => {
    try {
      await notificationsAPI.markRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      // Decrement unread count
      setUnreadNotificationCount(prev => Math.max(0, prev - 1));
    } catch (err: any) {
      console.warn('Failed to mark notification as read:', err);
    }
  }, [setUnreadNotificationCount]);

  const markAllAsRead = useCallback(async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadNotificationCount(0);
    } catch (err: any) {
      console.warn('Failed to mark all notifications as read:', err);
    }
  }, [setUnreadNotificationCount]);

  // Fetch unread count on mount
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  return {
    notifications,
    unreadCount: unreadNotificationCount,
    latestNotification,
    loading,
    error,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    clearLatestNotification,
  };
}
